import React from 'react'
import {useIntl} from 'react-intl'
import {useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {getUserMeasurementSystem} from 'modules/meta/selectors'

import {Box} from '@vio/atlas/helpers/Box'
import {Icon} from '@vio/atlas/Icon'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'

import type {LocationContentVariant} from './types'
import type {ContentHotel} from '@findhotel/sapi'

interface LocationAttractionsProps {
  attractions: ContentHotel['location']['poi']
  variant: LocationContentVariant
}

const AttractionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: ${cssTheme.layout.spacing.s250};
  column-gap: ${cssTheme.layout.spacing.s500};
  &.--is-mounted-on-ap {
    display: block;
    column-count: 2;
    column-fill: balance;

    & > * {
      break-inside: avoid;
      -webkit-column-break-inside: avoid;
      page-break-inside: avoid;
      margin-bottom: ${cssTheme.layout.spacing.s250};
    }
  }
`

const DistanceText = styled.span`
  margin-left: ${cssTheme.layout.spacing.s200};
  color: ${cssTheme.colors.content.neutral.secondary};
  white-space: nowrap;
`

const BulletedListWrapper = styled.ul`
  display: flex;
  flex-direction: column;
  row-gap: ${cssTheme.layout.spacing.s250};
  margin: 0;
  padding-left: ${cssTheme.layout.spacing.s600};
`

const MAX_ATTRACTIONS_ON_DISPLAY = 6

export const LocationAttractions = ({
  attractions,
  variant
}: LocationAttractionsProps) => {
  if (!attractions) return null

  const isAPVariant =
    variant === 'ap' || variant === 'apMobile' || variant === 'apCombined'

  const attractionsToDisplay = attractions.slice(
    0,
    isAPVariant ? MAX_ATTRACTIONS_ON_DISPLAY : undefined
  )

  switch (variant) {
    case 'amenitiesTab':
      return (
        <BulletedListWrapper>
          {attractionsToDisplay.map((attraction, index) => (
            <li key={`attractions-item-${index}`}>
              <AttractionItem attraction={attraction} />
            </li>
          ))}
        </BulletedListWrapper>
      )
    case 'ap':
    case 'apMobile':
      return (
        <AttractionsWrapper
          className={variant === 'ap' ? '--is-mounted-on-ap' : undefined}
        >
          {attractionsToDisplay.map((attraction, index) => (
            <Box
              display="flex"
              alignItems="flex-start"
              gap="s300"
              key={`attractions-item-${index}`}
            >
              <Box marginTop="s200">
                <Icon
                  colorPath="content.neutral.secondary"
                  size="md"
                  name="Pin"
                />
              </Box>
              <AttractionItem attraction={attraction} />
            </Box>
          ))}
        </AttractionsWrapper>
      )
    case 'apCombined':
      return (
        <AttractionsWrapper>
          {attractionsToDisplay.map((attraction, index) => (
            <CompactAttractionRow
              attraction={attraction}
              key={`attractions-item-${index}`}
            />
          ))}
        </AttractionsWrapper>
      )
    default:
      return (
        <AttractionsWrapper>
          {attractionsToDisplay.map((attraction, index) => (
            <Box
              display="flex"
              alignItems="flex-start"
              gap="s300"
              key={`attractions-item-${index}`}
            >
              <AttractionItem attraction={attraction} />
            </Box>
          ))}
        </AttractionsWrapper>
      )
  }
}

type Attraction = ContentHotel['location']['poi'][number]

const useAttractionDistance = (attraction: Attraction) => {
  const userMeasurementSystem = useSelector(getUserMeasurementSystem)
  const {formatMessage} = useIntl()

  const isMileUnit = userMeasurementSystem === 'imperial'
  const unitMessage = formatMessage(
    isMileUnit
      ? {id: 'distanceAsMiles', defaultMessage: 'miles'}
      : {id: 'distanceAsKm', defaultMessage: 'km'}
  )
  const value = isMileUnit ? attraction.distance_miles : attraction.distance_km

  return `(${value} ${unitMessage})`
}

const AttractionItem = ({attraction}: {attraction: Attraction}) => {
  const distance = useAttractionDistance(attraction)

  return (
    <Text colorPath="content.neutral.primary">
      <bdi>{attraction.name}</bdi>
      <DistanceText>{distance}</DistanceText>
    </Text>
  )
}

const CompactAttractionRow = ({attraction}: {attraction: Attraction}) => {
  const distance = useAttractionDistance(attraction)

  return (
    <Box display="flex" alignItems="flex-start" gap="s300" minWidth="0">
      <Icon colorPath="content.neutral.secondary" size="md" name="Pin" />
      <Text variant="bodyS" colorPath="content.neutral.primary">
        <bdi>{attraction.name}</bdi>{' '}
        <Text
          as="span"
          variant="bodyS"
          colorPath="content.neutral.secondary"
          style={{whiteSpace: 'nowrap'}}
        >
          {distance}
        </Text>
      </Text>
    </Box>
  )
}
